// components/ventas/SeleccionTallerHorario.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { App, Button, Spin, Alert, Input, Empty, Tooltip, Select } from 'antd';
import {
  SearchOutlined, ClockCircleOutlined, CheckOutlined,
  ShoppingCartOutlined, ArrowRightOutlined, DeleteOutlined,
  HomeOutlined, LockOutlined, PlusOutlined,
  CalendarOutlined, TeamOutlined
} from '@ant-design/icons';
import VentasPresenciales from '../../../../service/VentaPresencial';
import { useNotify } from '../../../../providers/NotificationProvider';

const { Option } = Select;

// Firma del carrito para saber si cambió respecto de las ventas ya creadas.
const firmaCarrito = (lista) => lista
  .map(c => `${c.taller?._id || c.tallerId}:${[...(c.horarioIds || [])].sort().join(',')}`)
  .sort().join('|');

// Libera las reservas de ventas que ya no se van a usar. Errores silenciosos: si
// alguna ya expiró o se canceló, el backend lo informa y el cron limpia el resto.
const cancelarVentas = async (ids, motivo) => {
  for (const id of ids.filter(Boolean)) {
    try { await VentasPresenciales.CancelarVenta(id, { motivo }); } catch { /* ver arriba */ }
  }
};

const SeleccionTallerHorario = ({ ventaData, updateVentaData, nextStep }) => {
  const notify = useNotify();
  const { modal } = App.useApp();
  const [loading, setLoading]               = useState(false);
  const [talleres, setTalleres]             = useState([]);
  const [tallerActual, setTallerActual]     = useState(null);
  const [horariosActuales, setHorariosActuales] = useState([]);
  const [carrito, setCarrito]               = useState(
    ventaData.items?.map(i => ({ taller: i.taller, horarioIds: i.horarioIds })) || []
  );
  const [searchTerm, setSearchTerm]         = useState('');
  const [recintoFiltro, setRecintoFiltro]   = useState('all');

  useEffect(() => { cargarTalleres(); }, []);

  const cargarTalleres = async () => {
    try {
      setLoading(true);
      const response = await VentasPresenciales.ObtenerTalleresDisponibles();
      if (response.success) setTalleres(response.talleres || []);
    } catch (error) {
      notify.error(error.message || 'Error al cargar los talleres');
    } finally {
      setLoading(false);
    }
  };

  // ── Computed ───────────────────────────────────────────────────────────────
  const recintos = useMemo(() => {
    const nombres = [...new Set(talleres.map(t => t.recinto?.nombre).filter(Boolean))];
    return nombres.sort();
  }, [talleres]);

  const talleresFiltrados = useMemo(() => talleres.filter(t => {
    const matchSearch  = t.nombre.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRecinto = recintoFiltro === 'all' || t.recinto?.nombre === recintoFiltro;
    const yaEnCarrito  = carrito.some(c => c.taller._id === t._id);
    return matchSearch && matchRecinto && !yaEnCarrito;
  }), [talleres, searchTerm, recintoFiltro, carrito]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSeleccionarTaller = (taller) => {
    setTallerActual(taller);
    setHorariosActuales([]);
  };

  const handleToggleHorario = (horarioId) => {
    setHorariosActuales(prev =>
      prev.includes(horarioId) ? prev.filter(id => id !== horarioId) : [...prev, horarioId]
    );
  };

  const handleAgregarAlCarrito = () => {
    if (!tallerActual || horariosActuales.length === 0) {
      notify.warning('Selecciona al menos un horario');
      return;
    }
    setCarrito(prev => [...prev, { taller: tallerActual, horarioIds: horariosActuales }]);
    setTallerActual(null);
    setHorariosActuales([]);
    notify.success(`${tallerActual.nombre} agregado al carrito`);
  };

  const handleQuitarDelCarrito = (idx) => {
    setCarrito(prev => prev.filter((_, i) => i !== idx));
  };

  const getAlertasCarritoItem = (item) => {
    const llenos = item.horarioIds
      .map(id => item.taller.horarios?.find(h => h._id === id))
      .filter(Boolean)
      .filter(h => !h.cuposIlimitados && (h.cuposOcupados || 0) + (h.cuposReservados || 0) >= (h.cupos || 0));
    const sobrecupo = item.horarioIds
      .map(id => item.taller.horarios?.find(h => h._id === id))
      .filter(Boolean)
      .filter(h => !h.cuposIlimitados && (h.cuposOcupados || 0) > (h.cupos || 0));
    return { llenos, sobrecupo };
  };

  const ejecutarVentas = async () => {
    const itemsPrevios = ventaData.items || [];
    // Volvió desde un paso posterior sin tocar el carrito: las ventas (y sus cupos
    // reservados) siguen sirviendo. Antes se creaban otras y las primeras quedaban
    // reteniendo cupos hasta expirar.
    if (itemsPrevios.length > 0 && firmaCarrito(itemsPrevios) === firmaCarrito(carrito)) {
      nextStep();
      return;
    }

    setLoading(true);
    const items = [];
    const todasAdvertencias = [];
    try {
      // El carrito cambió: liberar las reservas anteriores antes de crear las nuevas.
      if (itemsPrevios.length > 0) {
        await cancelarVentas(itemsPrevios.map(i => i.ventaId), 'Carrito modificado por el vendedor');
      }
      for (const carritoItem of carrito) {
        const response = await VentasPresenciales.IniciarVenta({
          tallerId: carritoItem.taller._id,
          horarioIds: carritoItem.horarioIds
        });
        if (!response.success)
          throw new Error(`Error al iniciar venta para ${carritoItem.taller.nombre}`);
        items.push({
          tallerId:    carritoItem.taller._id,
          horarioIds:  carritoItem.horarioIds,
          taller:      response.data.taller,
          horarios:    response.data.horarios,
          ventaId:     response.data.ventaId,
          codigoVenta: response.data.codigoVenta,
          expiraEn:    response.data.expiraEn,
        });
        if (response.data.advertencias?.length)
          todasAdvertencias.push(
            ...response.data.advertencias.map(a => `[${carritoItem.taller.nombre}] ${a}`)
          );
      }
      const expiraEnMin = items.reduce(
        (min, i) => !min || new Date(i.expiraEn) < new Date(min) ? i.expiraEn : min, null
      );
      updateVentaData({
        items,
        ventaId: items[0].ventaId, codigoVenta: items[0].codigoVenta,
        tallerId: items[0].tallerId, horarioIds: items[0].horarioIds,
        taller: items[0].taller, horarios: items[0].horarios,
        expiraEn: expiraEnMin, advertencias: todasAdvertencias,
        // Ventas nuevas: el cliente y el plan hay que volver a asociarlos en el backend.
        ...(itemsPrevios.length > 0 ? {
          usuario: null, inscrito: null, esVecino: false, cargas: [],
          planNombre: null, planDetalle: null, codigoDescuentoManual: null, descuentoManual: null
        } : {})
      });
      if (todasAdvertencias.length > 0) {
        modal.warning({
          title: 'Advertencias del servidor',
          width: 520,
          content: (
            <div className="space-y-2 mt-3">
              {todasAdvertencias.map((adv, i) => (
                <div key={i} className="text-sm bg-amber-50 border border-amber-200
                  text-amber-800 rounded-lg px-3 py-2">{adv}</div>
              ))}
            </div>
          ),
          okText: 'Entendido, continuar',
          onOk: () => { notify.success(`${items.length} taller(es) iniciado(s)`); nextStep(); }
        });
      } else {
        notify.success(`${items.length} taller(es) iniciado(s) correctamente`);
        nextStep();
      }
    } catch (error) {
      // Si falló a mitad (p. ej. el 2º taller sin cupo), las ventas ya creadas
      // quedaban reservando cupos sin que nadie pudiera usarlas.
      if (items.length > 0) {
        await cancelarVentas(items.map(i => i.ventaId), 'Inicio de venta incompleto');
      }
      updateVentaData({ items: [], ventaId: null, codigoVenta: null, expiraEn: null });
      notify.error(error.message || 'No se pudieron iniciar todas las ventas');
    } finally {
      setLoading(false);
    }
  };

  const handleContinuar = () => {
    if (carrito.length === 0) { notify.warning('Agrega al menos un taller al carrito'); return; }
    const sobrecuposBloqueantes = [];
    for (const item of carrito) {
      const { sobrecupo } = getAlertasCarritoItem(item);
      sobrecupo.forEach(h => sobrecuposBloqueantes.push({ taller: item.taller.nombre, horario: h }));
    }
    if (sobrecuposBloqueantes.length > 0) {
      modal.error({
        title: 'Sobrecupo detectado — venta bloqueada',
        width: 520,
        content: (
          <div className="space-y-2 mt-3">
            <p className="text-slate-500 text-sm">
              Estos horarios tienen más inscritos del máximo permitido:
            </p>
            {sobrecuposBloqueantes.map((s, i) => (
              <div key={i} className="text-sm bg-red-50 border border-red-200 text-red-700
                rounded-lg px-3 py-2">
                <strong>[{s.taller}]</strong> {s.horario.nombre || s.horario.dia} —{' '}
                {s.horario.cuposOcupados}/{s.horario.cupos} cupos
              </div>
            ))}
          </div>
        ),
        okText: 'Entendido',
      });
      return;
    }
    const llenosTotal = [];
    for (const item of carrito) {
      const { llenos } = getAlertasCarritoItem(item);
      llenos.forEach(h => llenosTotal.push({ taller: item.taller.nombre, horario: h }));
    }
    if (llenosTotal.length > 0) {
      modal.confirm({
        title: 'Horarios sin cupos disponibles',
        width: 520,
        content: (
          <div className="space-y-2 mt-3">
            <p className="text-slate-500 text-sm">
              Estos horarios están llenos. Se inscribirá en sobrecupo:
            </p>
            {llenosTotal.map((s, i) => (
              <div key={i} className="text-sm bg-amber-50 border border-amber-200
                text-amber-800 rounded-lg px-3 py-2">
                <strong>[{s.taller}]</strong> {s.horario.nombre || s.horario.dia} —{' '}
                {s.horario.cuposOcupados}/{s.horario.cupos}
              </div>
            ))}
            <Alert type="warning" showIcon
              message="Esta acción quedará registrada. Confirma solo si tienes autorización." />
          </div>
        ),
        okText: 'Confirmar igualmente',
        okButtonProps: { danger: true },
        cancelText: 'Cancelar',
        onOk: ejecutarVentas,
      });
      return;
    }
    ejecutarVentas();
  };

  // ── Helpers visuales ───────────────────────────────────────────────────────
  const calcularCupos = (h) => {
    if (h.cuposIlimitados) return { esIlimitado: true, disponibles: null, ocupados: 0, total: 0, porcentaje: 0, sobrecupo: false };
    const total    = h.cupos || 0;
    const ocupados = (h.cuposOcupados || 0) + (h.cuposReservados || 0);
    const disp     = Math.max(0, total - ocupados);
    const sobre    = (h.cuposOcupados || 0) > total;
    return { esIlimitado: false, disponibles: disp, ocupados, total, porcentaje: total > 0 ? Math.min(100, (ocupados / total) * 100) : 0, sobrecupo: sobre };
  };

  const getRangoEdad = (t) => {
    if (!t.edadMin && !t.edadMax) return null;
    if (t.edadMin && !t.edadMax) return `${t.edadMin}+ años`;
    if (!t.edadMin && t.edadMax) return `Hasta ${t.edadMax} años`;
    return `${t.edadMin}–${t.edadMax} años`;
  };

  const getEstadoBadge = (taller) => {
    const s = taller.estadoInscripcion;
    if (s === 'disponible')
      return <span className="inline-flex items-center gap-1 text-xs bg-blue-100
        text-blue-700 font-medium px-2 py-0.5 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
        Abierto
      </span>;
    if (s === 'proximamente')
      return <Tooltip title={taller.mensajeInscripcion}>
        <span className="inline-flex items-center gap-1 text-xs bg-amber-100
          text-amber-700 font-medium px-2 py-0.5 rounded-full cursor-help">
          <ClockCircleOutlined style={{ fontSize: 10 }} />
          Próximamente
        </span>
      </Tooltip>;
    if (s === 'cerrado')
      return <Tooltip title="Cerrado al público — admin puede inscribir">
        <span className="inline-flex items-center gap-1 text-xs bg-slate-100
          text-slate-600 font-medium px-2 py-0.5 rounded-full cursor-help">
          <LockOutlined style={{ fontSize: 10 }} />
          Cerrado
        </span>
      </Tooltip>;
    return <span className="inline-flex items-center gap-1 text-xs bg-slate-100
      text-slate-500 font-medium px-2 py-0.5 rounded-full">
      <LockOutlined style={{ fontSize: 10 }} />
      Fuera de ventana
    </span>;
  };

  const getDiasPills = (h) => {
    const dias = h.bloques
      ? [...new Set(h.bloques.flatMap(b => b.dias))]
      : [h.dia, ...(h.dias || [])].filter(Boolean);
    return dias;
  };

  const getHorarioTimes = (h) => {
    if (h.bloques?.length > 0)
      return h.bloques.map(b => `${b.inicio} – ${b.fin}`).join(' / ');
    return `${h.inicio} – ${h.fin}`;
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Spin spinning={loading}>
      <div className="flex flex-col gap-4">

        {/* ── Barra de filtros ──────────────────────────────────────────── */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <div className="flex items-center gap-2 text-slate-500 text-sm font-medium flex-shrink-0">
              <HomeOutlined />
              <Select
                value={recintoFiltro}
                onChange={setRecintoFiltro}
                style={{ minWidth: 200 }}
                bordered={false}
                className="font-medium"
              >
                <Option value="all">Todos los recintos</Option>
                {recintos.map(r => <Option key={r} value={r}>{r}</Option>)}
              </Select>
            </div>
            <div className="h-5 w-px bg-slate-200 hidden sm:block" />
            <Input
              placeholder="Buscar taller por nombre..."
              prefix={<SearchOutlined className="text-slate-400" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
              bordered={false}
              className="flex-1"
            />
            <div className="flex-shrink-0">
              <span className="text-xs font-semibold text-slate-500 bg-slate-100
                px-3 py-1.5 rounded-full">
                {talleresFiltrados.length} de {talleres.length} talleres
              </span>
            </div>
          </div>
        </div>

        {/* ── Layout principal: dos columnas ────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ minHeight: 580 }}>

          {/* ── COL IZQUIERDA: Lista de talleres ──────────────────────── */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl
            overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center
              justify-between flex-shrink-0">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Talleres disponibles
              </span>
              {tallerActual && (
                <button
                  onClick={() => { setTallerActual(null); setHorariosActuales([]); }}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Ver lista completa
                </button>
              )}
            </div>

            <div className="flex-1 overflow-y-auto">
              {talleresFiltrados.length === 0 ? (
                <div className="flex items-center justify-center h-48">
                  <Empty
                    description={
                      <span className="text-slate-400 text-sm">
                        {searchTerm || recintoFiltro !== 'all'
                          ? 'Sin resultados para este filtro'
                          : 'No hay talleres disponibles'}
                      </span>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              ) : (
                <div>
                  {talleresFiltrados.map((t, idx) => {
                    const isSelected = tallerActual?._id === t._id;
                    return (
                      <div
                        key={t._id}
                        onClick={() => handleSeleccionarTaller(t)}
                        className={`px-4 py-3 cursor-pointer transition-all border-b
                          border-slate-50 last:border-0 hover:bg-slate-50 ${
                          isSelected
                            ? 'bg-[#fffbea] border-l-4 border-l-[#feda2c] pl-3'
                            : 'border-l-2 border-l-transparent'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm font-semibold truncate ${
                              isSelected ? 'text-[#00266b]' : 'text-slate-800'
                            }`}>
                              {t.nombre}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {t.recinto?.nombre && (
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                  <HomeOutlined style={{ fontSize: 10 }} />
                                  {t.recinto.nombre}
                                </span>
                              )}
                              {getRangoEdad(t) && (
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                  <TeamOutlined style={{ fontSize: 10 }} />
                                  {getRangoEdad(t)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {getEstadoBadge(t)}
                              <span className="text-xs text-slate-400">
                                {t.horarios?.length || 0} horarios
                              </span>
                            </div>
                          </div>
                          <ArrowRightOutlined className={`flex-shrink-0 mt-1 text-xs ${
                            isSelected ? 'text-[#0047bb]' : 'text-slate-300'
                          }`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── COL DERECHA: Horarios + Carrito ───────────────────────── */}
          <div className="lg:col-span-3 flex flex-col gap-4">

            {/* Panel de horarios (visible si hay taller seleccionado) */}
            {tallerActual ? (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex-shrink-0">
                {/* Header del taller seleccionado */}
                <div className="px-5 py-4 border-b border-slate-100 bg-[#00266b] text-white">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-bold text-white">{tallerActual.nombre}</div>
                      <div className="flex items-center gap-3 mt-1 text-sm text-[#b9c8e8] flex-wrap">
                        {tallerActual.recinto?.nombre && (
                          <span className="flex items-center gap-1">
                            <HomeOutlined style={{ fontSize: 11 }} />
                            {tallerActual.recinto.nombre}
                          </span>
                        )}
                        {getRangoEdad(tallerActual) && (
                          <span className="flex items-center gap-1">
                            <TeamOutlined style={{ fontSize: 11 }} />
                            {getRangoEdad(tallerActual)}
                          </span>
                        )}
                        {getEstadoBadge(tallerActual)}
                      </div>
                    </div>
                    <Button
                      size="small" type="text"
                      onClick={() => { setTallerActual(null); setHorariosActuales([]); }}
                      className="!text-[#fae57e] hover:!text-white flex-shrink-0"
                    >
                      Cambiar
                    </Button>
                  </div>
                </div>

                {/* Horarios */}
                <div className="p-4">
                  <div className="text-xs font-semibold text-slate-500 uppercase
                    tracking-wide mb-3 flex items-center gap-2">
                    <CalendarOutlined />
                    Selecciona los horarios
                    {horariosActuales.length > 0 && (
                      <span className="bg-blue-100 text-blue-700 px-2 py-0.5
                        rounded-full text-xs font-bold">
                        {horariosActuales.length} seleccionado(s)
                      </span>
                    )}
                  </div>

                  {tallerActual.horarios?.length > 0 ? (
                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                      {tallerActual.horarios.map(h => {
                        const cupos  = calcularCupos(h);
                        const sel    = horariosActuales.includes(h._id);
                        const dias   = getDiasPills(h);
                        const tiempo = getHorarioTimes(h);
                        const cuposColor = cupos.esIlimitado ? 'bg-blue-400'
                          : cupos.sobrecupo   ? 'bg-red-500'
                          : cupos.disponibles === 0 ? 'bg-amber-400'
                          : cupos.disponibles < 4 ? 'bg-amber-400'
                          : 'bg-blue-400';

                        return (
                          <div
                            key={h._id}
                            onClick={() => handleToggleHorario(h._id)}
                            className={`p-3 rounded-xl border cursor-pointer
                              transition-all select-none ${
                              sel
                                ? 'border-[#0047bb] bg-blue-50 ring-2 ring-[#feda2c]/60'
                                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {/* Checkbox custom */}
                              <div className={`w-5 h-5 rounded border-2 flex items-center
                                justify-center flex-shrink-0 mt-0.5 transition-all ${
                                sel
                                  ? 'bg-[#0047bb] border-[#0047bb]'
                                  : 'border-slate-300 bg-white'
                              }`}>
                                {sel && <CheckOutlined style={{ fontSize: 10, color: 'white' }} />}
                              </div>

                              <div className="flex-1 min-w-0">
                                {/* Días */}
                                <div className="flex flex-wrap gap-1 mb-1">
                                  {dias.map((d, i) => (
                                    <span key={i} className={`text-xs px-2 py-0.5 rounded-full
                                      capitalize font-medium ${
                                      sel
                                        ? 'bg-blue-100 text-blue-700'
                                        : 'bg-slate-100 text-slate-600'
                                    }`}>{d}</span>
                                  ))}
                                </div>

                                {/* Horario */}
                                <div className={`text-sm font-bold ${
                                  sel ? 'text-blue-800' : 'text-slate-700'
                                }`}>{tiempo}</div>

                                {/* Cupos */}
                                {!cupos.esIlimitado && (
                                  <div className="mt-2">
                                    <div className="flex justify-between text-xs
                                      text-slate-400 mb-1">
                                      <span>
                                        {cupos.sobrecupo
                                          ? '⚠ Sobrecupo'
                                          : cupos.disponibles === 0
                                          ? 'Sin cupos'
                                          : `${cupos.disponibles} disponibles`}
                                      </span>
                                      <span className="font-mono">
                                        {cupos.ocupados}/{cupos.total}
                                      </span>
                                    </div>
                                    <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${cuposColor}`}
                                        style={{ width: `${cupos.porcentaje}%` }}
                                      />
                                    </div>
                                  </div>
                                )}
                                {cupos.esIlimitado && (
                                  <span className="text-xs text-blue-600 font-medium mt-1 block">
                                    ∞ Cupos ilimitados
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 text-slate-400 text-sm">
                      Este taller no tiene horarios configurados.
                    </div>
                  )}

                  {/* Agregar al carrito */}
                  {tallerActual.horarios?.length > 0 && (
                    <Button
                      type="primary" block
                      icon={<PlusOutlined />}
                      onClick={handleAgregarAlCarrito}
                      disabled={horariosActuales.length === 0}
                      className="mt-4 h-10 font-semibold"
                    >
                      Agregar al carrito
                      {horariosActuales.length > 0 && ` (${horariosActuales.length} horario${horariosActuales.length > 1 ? 's' : ''})`}
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              /* Placeholder cuando no hay taller seleccionado */
              <div className="bg-white border border-dashed border-slate-300 rounded-xl
                flex flex-col items-center justify-center py-12 flex-shrink-0">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center
                  justify-center mb-3">
                  <ArrowRightOutlined className="text-slate-400 text-lg" />
                </div>
                <p className="text-slate-500 text-sm font-medium">
                  Selecciona un taller de la lista
                </p>
                <p className="text-slate-400 text-xs mt-1">
                  Se mostrarán los horarios disponibles aquí
                </p>
              </div>
            )}

            {/* ── Carrito ─────────────────────────────────────────────── */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden flex-1">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center
                justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCartOutlined className="text-slate-500" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                    Carrito
                  </span>
                  {carrito.length > 0 && (
                    <span className="bg-[#feda2c] text-[#5c4300] text-xs font-bold
                      w-5 h-5 rounded-full flex items-center justify-center">
                      {carrito.length}
                    </span>
                  )}
                </div>
                {carrito.length > 0 && (
                  <span className="text-xs text-slate-400">
                    {carrito.reduce((s, c) => s + c.horarioIds.length, 0)} horarios en total
                  </span>
                )}
              </div>

              {carrito.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <ShoppingCartOutlined className="text-3xl text-slate-300 mb-2" />
                  <p className="text-slate-400 text-sm">El carrito está vacío</p>
                  <p className="text-slate-300 text-xs mt-1">
                    Selecciona talleres y agrégalos aquí
                  </p>
                </div>
              ) : (
                <div>
                  {/* Items del carrito */}
                  <div className="divide-y divide-slate-100">
                    {carrito.map((item, idx) => {
                      const { llenos, sobrecupo } = getAlertasCarritoItem(item);
                      return (
                        <div key={idx} className="px-4 py-3 flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 text-sm truncate">
                              {item.taller.nombre}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-xs text-slate-400">
                                {item.horarioIds.length} horario{item.horarioIds.length > 1 ? 's' : ''}
                              </span>
                              {item.taller.recinto?.nombre && (
                                <span className="text-xs text-slate-400">
                                  · {item.taller.recinto.nombre}
                                </span>
                              )}
                              {sobrecupo.length > 0 && (
                                <span className="text-xs bg-red-100 text-red-600 font-medium
                                  px-1.5 py-0.5 rounded">
                                  Sobrecupo
                                </span>
                              )}
                              {llenos.length > 0 && sobrecupo.length === 0 && (
                                <span className="text-xs bg-amber-100 text-amber-600 font-medium
                                  px-1.5 py-0.5 rounded">
                                  Sin cupos
                                </span>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => handleQuitarDelCarrito(idx)}
                            className="w-7 h-7 rounded-lg flex items-center justify-center
                              text-slate-400 hover:text-red-500 hover:bg-red-50
                              transition-colors flex-shrink-0"
                          >
                            <DeleteOutlined style={{ fontSize: 14 }} />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  {/* Continuar */}
                  <div className="p-4 border-t border-slate-100">
                    <Button
                      type="primary" size="large" block
                      icon={<ArrowRightOutlined />}
                      onClick={handleContinuar}
                      loading={loading}
                      className="h-11 font-semibold text-base"
                      style={{ background: '#feda2c', borderColor: '#feda2c', color: '#00266b' }}
                    >
                      Continuar con {carrito.length} taller{carrito.length > 1 ? 'es' : ''}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Spin>
  );
};

export default SeleccionTallerHorario;
